import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import type { User } from "@relais/contracts";
import { supabase } from "./supabase.js";
import { apiFetch } from "./api.js";

export type SessionState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; session: Session };

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setState(
        data.session
          ? { status: "authed", session: data.session }
          : { status: "anon" },
      );
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setState(session ? { status: "authed", session } : { status: "anon" });
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

export async function signOut() {
  await supabase.auth.signOut();
  cachedProfile = null;
  cachedProfileUserId = null;
}

// Lazy cache so multiple components don't refetch.
let cachedProfile: User | null = null;
let cachedProfileUserId: string | null = null;
let inflight: Promise<User> | null = null;

// Après un PATCH /users/me réussi, on garde le cache aligné avec le serveur.
export function setCachedProfile(profile: User) {
  cachedProfile = profile;
  cachedProfileUserId = profile.id;
}

export type UserProfileState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; profile: User };

export function useUserProfile(): UserProfileState {
  const session = useSession();
  const userId =
    session.status === "authed" ? session.session.user.id : null;
  const [state, setState] = useState<UserProfileState>(() => {
    if (cachedProfile && cachedProfileUserId === userId) {
      return { status: "ready", profile: cachedProfile };
    }
    return { status: "loading" };
  });

  useEffect(() => {
    if (!userId) {
      setState({ status: "loading" });
      return;
    }
    if (cachedProfile && cachedProfileUserId === userId) {
      setState({ status: "ready", profile: cachedProfile });
      return;
    }
    let cancelled = false;
    const p =
      inflight ??
      (inflight = apiFetch<User>("/users/me").finally(() => {
        // clear inflight once settled so future calls can refetch on demand
      }));
    p.then(
      (profile) => {
        if (cancelled) return;
        cachedProfile = profile;
        cachedProfileUserId = userId;
        inflight = null;
        setState({ status: "ready", profile });
      },
      () => {
        if (cancelled) return;
        inflight = null;
        setState({ status: "error" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return state;
}

/**
 * Vérifie le rôle du compte connecté auprès de l'API. Utilisé au login pour
 * éjecter tout compte non-poseur (mirror du garde admin-front, commit
 * c0a8ebe) : un conducteur ou un admin n'a rien à faire sur Poseurs.
 */
export async function fetchRole(): Promise<User["role"]> {
  const profile = await apiFetch<User>("/users/me");
  cachedProfile = profile;
  cachedProfileUserId = profile.id;
  return profile.role;
}
